import React, { useEffect, useState, useRef } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import styles from './GoogleBusiness.module.css';
import Loading from '../../../../components/loading/Loading.js';
import InputField from "../../../../components/inputfield/InputField";
import CustomButton from "../../../../components/buttons/CustomButton";
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import { Typography, Tooltip, Alert, Box, Paper, Divider } from "@mui/material";
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';
import usePlanLock from '../../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../consts/SubscriptionFeatures';
import GoogleIcon from '@mui/icons-material/Google';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';

const GoogleBusiness = () => {
    const dataProvider = DataProviderInstance();
    const { hasRole } = useRoleAccess();
    const [localLoading, setLocalLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    
    // Verificar se o usuário tem permissão para editar
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
    // Cadeado por plano: bloqueado só quando a clínica não tem nem o convite pelo
    // número da Gabriela (platform_engagement) nem número próprio (zapi_integration),
    // o mesmo critério de company/google-business/set.php.
    const lock = usePlanLock(
        [SUBSCRIPTION_PERMISSIONS.PLATFORM_ENGAGEMENT, SUBSCRIPTION_PERMISSIONS.ZAPI_INTEGRATION],
        'O convite para avaliar no Google'
    );

    const [editing, setEditing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [config, setConfig] = useState(null);
    const [hasConfig, setHasConfig] = useState(false);

    // Form refs
    const inputFieldGoogleUrlRef = useRef(null);
    const inputFieldMaskedUrlRef = useRef(null);

    // Form state
    const [googleReviewUrl, setGoogleReviewUrl] = useState('');
    const [maskedUrl, setMaskedUrl] = useState('');
    const [urlValidation, setUrlValidation] = useState({ isValid: true, message: '' });
    const [maskedUrlValidation, setMaskedUrlValidation] = useState({ isValid: true, message: '' });

    // Feedback states
    const [saveResult, setSaveResult] = useState(null);

    // Fetch configuration when component mounts
    useEffect(() => {
        fetchGoogleBusinessConfig();
    }, []);

    // Initialize data when config changes
    useEffect(() => {
        if (config) {
            setGoogleReviewUrl(config.google_review_url || '');
            setMaskedUrl(config.masked_url || '');
            
            // Update input fields if they exist
            if (!initialDataLoaded) {
                setTimeout(() => {
                    updateInputFields();
                    setInitialDataLoaded(true);
                }, 100);
            }
        }
    }, [config, initialDataLoaded]);

    const fetchGoogleBusinessConfig = () => {
        setLocalLoading(true);
        
        dataProvider.GetGoogleBusinessConfig((data) => {
            if (data.success) {
                if (data.config) {
                    setConfig(data.config);
                    setHasConfig(true);
                } else {
                    setConfig(null);
                    setHasConfig(false);
                }
            } else {
                
                setSaveResult({
                    success: false,
                    message: data.message || 'Erro ao carregar configuração'
                });
            }
            setLocalLoading(false);
        });
    };

    const updateInputFields = () => {
        if (!config) return;
        
        const fieldsToUpdate = [
            { ref: inputFieldGoogleUrlRef, value: config.google_review_url },
            { ref: inputFieldMaskedUrlRef, value: config.masked_url }
        ];
        
        fieldsToUpdate.forEach(({ ref, value }) => {
            if (ref && ref.current && typeof ref.current.SetValue === 'function') {
                ref.current.SetValue(value || '');
            }
        });
    };

    // Validação de URL do Google Meu Negócio em tempo real
    const validateGoogleBusinessUrl = (url) => {
        if (!url || url.trim() === '') {
            return { isValid: true, message: '' };
        }

        const trimmedUrl = url.trim();
        
        // Verifica se é uma URL válida
        try {
            new URL(trimmedUrl);
        } catch {
            return { isValid: false, message: 'URL inválida' };
        }

        // Padrões aceitos para URLs do Google Meu Negócio
        const allowedPatterns = [
            /^https:\/\/g\.page\//,                           // g.page links
            /^https:\/\/maps\.google\.com\//,                 // Google Maps links
            /^https:\/\/www\.google\.com\/maps\/place\//,     // Google Maps place links
            /^https:\/\/business\.google\.com\//,             // Google Business links
            /^https:\/\/maps\.app\.goo\.gl\//,                // Short Google Maps links
            /^https:\/\/goo\.gl\/maps\//                      // Legacy short links
        ];

        const isValidPattern = allowedPatterns.some(pattern => pattern.test(trimmedUrl));
        
        if (!isValidPattern) {
            return { 
                isValid: false, 
                message: 'Use URLs do Google como: g.page, maps.google.com, business.google.com, etc.' 
            };
        }

        return { isValid: true, message: 'URL válida do Google Meu Negócio' };
    };

    // Validação de URL mascarada
    const validateMaskedUrl = (url) => {
        if (!url || url.trim() === '') {
            return { isValid: true, message: '' };
        }

        try {
            new URL(url.trim());
            return { isValid: true, message: 'URL válida' };
        } catch {
            return { isValid: false, message: 'URL inválida' };
        }
    };

    // Função para testar links abrindo em nova aba
    const handleTestLink = (url) => {
        if (!url || url.trim() === '') {
            return;
        }

        const trimmedUrl = url.trim();
        
        // Validar se a URL é válida antes de abrir
        try {
            new URL(trimmedUrl);
            window.open(trimmedUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            
        }
    };

    // Verificar se o botão de teste deve estar habilitado
    const isTestButtonEnabled = (url, validation) => {
        return url && url.trim() !== '' && validation.isValid;
    };

    const onUpdateGoogleUrl = (value) => {
        setGoogleReviewUrl(value);
        const validation = validateGoogleBusinessUrl(value);
        setUrlValidation(validation);
        
        const hasChanged = value !== (config?.google_review_url || '');
        setHasChanges(hasChanged || maskedUrl !== (config?.masked_url || ''));
    };

    const onUpdateMaskedUrl = (value) => {
        setMaskedUrl(value);
        const validation = validateMaskedUrl(value);
        setMaskedUrlValidation(validation);
        
        const hasChanged = value !== (config?.masked_url || '');
        setHasChanges(hasChanged || googleReviewUrl !== (config?.google_review_url || ''));
    };

    const ActionButtons = () => (
        <div className={styles.actionButtons}>
            {editing ? (
                <>
                    <CustomButton
                        variant="contained"
                        label="Salvar"
                        style="secondary"
                        onClick={saveClickHandler}
                        startIcon={<SaveOutlinedIcon />}
                        disabled={!urlValidation.isValid || !maskedUrlValidation.isValid || !googleReviewUrl.trim()}
                    />
                    <CustomButton
                        variant="outlined"
                        label="Cancelar"
                        style="primary"
                        onClick={cancelClickHandler}
                        startIcon={<CloseOutlinedIcon />}
                    />
                </>
            ) : (
                <Tooltip
                    title={!canEdit ? "Apenas proprietários e administradores podem editar as configurações" : ""}
                    arrow
                >
                    <span>
                        <CustomButton
                            variant="contained"
                            label="Editar"
                            style="secondary"
                            onClick={lock.guard(() => setEditing(true))}
                            startIcon={<EditOutlinedIcon />}
                            disabled={!canEdit}
                        />
                    </span>
                </Tooltip>
            )}
        </div>
    );

    const cancelClickHandler = () => {
        setGoogleReviewUrl(config?.google_review_url || '');
        setMaskedUrl(config?.masked_url || '');
        setHasChanges(false);
        setEditing(false);
        setSaveResult(null);
        setUrlValidation({ isValid: true, message: '' });
        setMaskedUrlValidation({ isValid: true, message: '' });

        // Reset input fields
        if (inputFieldGoogleUrlRef.current) {
            inputFieldGoogleUrlRef.current.SetValue(config?.google_review_url || '');
        }
        if (inputFieldMaskedUrlRef.current) {
            inputFieldMaskedUrlRef.current.SetValue(config?.masked_url || '');
        }
    };

    const saveClickHandler = () => {
        if (lock.locked) { lock.openUpgrade(); return; }
        if (!urlValidation.isValid || !googleReviewUrl.trim()) {
            return;
        }

        setEditing(false);
        setLocalLoading(true);
        setSaveResult(null);

        dataProvider.SetGoogleBusinessConfig(
            googleReviewUrl.trim(),
            maskedUrl.trim(),
            (data) => {
                if (data.success) {
                    setSaveResult({
                        success: true,
                        message: data.message
                    });
                    setHasChanges(false);
                    
                    // Refresh configuration
                    fetchGoogleBusinessConfig();
                } else {
                    setSaveResult({
                        success: false,
                        message: data.message || 'Erro ao salvar configuração'
                    });
                }
                setLocalLoading(false);
            }
        );
    };

    const renderInstructions = () => (
        <Paper className={styles.instructionsContainer} elevation={1}>
            <div className={styles.instructionsHeader}>
                <InfoOutlinedIcon className={styles.instructionsIcon} />
                <Typography variant="h6" className={styles.instructionsTitle}>
                    Como obter a URL do Google Meu Negócio
                </Typography>
            </div>
            <Divider />
            <div className={styles.instructionsContent}>
                <div className={styles.instructionsLayout}>
                    <div className={styles.instructionsSteps}>
                        <Typography variant="body2" className={styles.instructionStep}>
                            <strong>1.</strong> Acesse o <a href="https://business.google.com" target="_blank" rel="noopener noreferrer">Google Meu Negócio</a>
                        </Typography>
                        <Typography variant="body2" className={styles.instructionStep}>
                            <strong>2.</strong> Selecione sua empresa
                        </Typography>
                        <Typography variant="body2" className={styles.instructionStep}>
                            <strong>3.</strong> Vá em "Clientes" → "Avaliações"
                        </Typography>
                        <Typography variant="body2" className={styles.instructionStep}>
                            <strong>4.</strong> Clique em "Obter mais avaliações"
                        </Typography>
                        <Typography variant="body2" className={styles.instructionStep}>
                            <strong>5.</strong> Copie o link compartilhável
                        </Typography>
                    </div>
                    <div className={styles.urlExamplesContainer}>
                        <Box className={styles.urlExamples}>
                            <Typography variant="caption" className={styles.examplesTitle}>
                                Exemplos de URLs aceitas:
                            </Typography>
                            <Typography variant="caption" className={styles.urlExample}>
                                • https://g.page/sua-empresa/review
                            </Typography>
                            <Typography variant="caption" className={styles.urlExample}>
                                • https://maps.google.com/...
                            </Typography>
                            <Typography variant="caption" className={styles.urlExample}>
                                • https://business.google.com/...
                            </Typography>
                        </Box>
                    </div>
                </div>
            </div>
        </Paper>
    );

    const renderFeedback = () => {
        if (!saveResult) return null;

        return (
            <Alert 
                severity={saveResult.success ? "success" : "error"}
                className={styles.feedbackAlert}
                icon={saveResult.success ? <CheckCircleOutlinedIcon /> : <ErrorOutlineIcon />}
            >
                {saveResult.message}
            </Alert>
        );
    };

    const businessIcon = <GoogleIcon />;

    return (
        <div className="page-content">
            <div className={styles.mainContainer}>
            <div className={styles.titleContainer}>
                <AreaTitle
                    Title="Google Meu Negócio"
                    Icon={businessIcon}
                    Description="Configure o link para avaliações do Google da sua empresa"
                />
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    {localLoading && (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Loading size={20} />
                            <Typography variant="caption" style={{ marginLeft: "8px" }}>
                                Salvando configuração...
                            </Typography>
                        </div>
                    )}
                    <ActionButtons />
                </div>
            </div>

            {lock.banner && <div style={{ marginBottom: 16 }}>{lock.banner}</div>}
            {lock.modal}

            {renderFeedback()}

            {/* Configuration Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <LinkIcon className={styles.sectionIcon} />
                    <div>
                        <Typography variant="h6" className={styles.sectionTitle}>
                            Configuração de URLs
                        </Typography>
                        <Typography variant="body2" className={styles.sectionDescription}>
                            Configure o link do Google Meu Negócio e opcionalmente uma URL personalizada
                        </Typography>
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <div className={styles.formField}>
                        <div className={styles.inputWithTestButton}>
                            <div className={styles.inputFieldContainer}>
                                <InputField
                                    Mandatory={true}
                                    Value={googleReviewUrl}
                                    Label="URL do Google Meu Negócio *"
                                    ref={inputFieldGoogleUrlRef}
                                    OnUpdateValue={onUpdateGoogleUrl}
                                    Disabled={!editing}
                                    placeholder="https://g.page/sua-empresa/review"
                                />
                            </div>
                            <div className={styles.testButtonContainer}>
                                <Tooltip title="Testar link abrindo em nova aba" arrow>
                                    <span>
                                        <CustomButton
                                            variant="outlined"
                                            label="Testar Link"
                                            style="primary"
                                            size="small"
                                            onClick={() => handleTestLink(googleReviewUrl)}
                                            startIcon={<LaunchIcon />}
                                            disabled={!isTestButtonEnabled(googleReviewUrl, urlValidation)}
                                            className={styles.testButton}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                        {googleReviewUrl && (
                            <div className={styles.validationMessage}>
                                {urlValidation.isValid ? (
                                    <Typography variant="caption" className={styles.validMessage}>
                                        <CheckCircleOutlinedIcon className={styles.validIcon} />
                                        {urlValidation.message}
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" className={styles.errorMessage}>
                                        <ErrorOutlineIcon className={styles.errorIcon} />
                                        {urlValidation.message}
                                    </Typography>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.formField}>
                        <div className={styles.inputWithTestButton}>
                            <div className={styles.inputFieldContainer}>
                                <InputField
                                    Mandatory={false}
                                    Value={maskedUrl}
                                    Label="URL Personalizada (Opcional)"
                                    ref={inputFieldMaskedUrlRef}
                                    OnUpdateValue={onUpdateMaskedUrl}
                                    Disabled={!editing}
                                    placeholder="https://seusite.com/avaliacoes"
                                />
                            </div>
                            <div className={styles.testButtonContainer}>
                                <Tooltip title="Testar link abrindo em nova aba" arrow>
                                    <span>
                                        <CustomButton
                                            variant="outlined"
                                            label="Testar Link"
                                            style="primary"
                                            size="small"
                                            onClick={() => handleTestLink(maskedUrl)}
                                            startIcon={<LaunchIcon />}
                                            disabled={!isTestButtonEnabled(maskedUrl, maskedUrlValidation)}
                                            className={styles.testButton}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                        <Typography variant="caption" className={styles.fieldHelp}>
                            URL personalizada que redirecionará para o Google Meu Negócio
                        </Typography>
                        {maskedUrl && !maskedUrlValidation.isValid && (
                            <div className={styles.validationMessage}>
                                <Typography variant="caption" className={styles.errorMessage}>
                                    <ErrorOutlineIcon className={styles.errorIcon} />
                                    {maskedUrlValidation.message}
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Instructions Section */}
            {renderInstructions()}
            </div>
        </div>
    );
};

export default GoogleBusiness;